import {
  Box,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import React from "react";

// Styled components
const CalendarGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(7, 1fr)",
  gap: 1,
  backgroundColor: theme.palette.divider,
  border: `1px solid ${theme.palette.divider}`,
}));

const DayHeader = styled(Box)(({ theme }) => ({
  textAlign: "center",
  fontWeight: 600,
  padding: theme.spacing(1),
  backgroundColor: theme.palette.grey[100],
  fontSize: "0.875rem",
}));

const CalendarDay = styled(Paper)(({ theme, isOtherMonth }) => ({
  backgroundColor: isOtherMonth
    ? theme.palette.grey[50]
    : theme.palette.background.paper,
  padding: theme.spacing(1),
  minHeight: 100,
  position: "relative",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  borderRadius: 0,
}));

const DayNumber = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  fontWeight: 500,
  color: theme.palette.text.secondary,
  textAlign: "right",
  position: "absolute",
  top: theme.spacing(0.5),
  right: theme.spacing(1),
}));

const ShiftLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.65rem",
  color: theme.palette.text.secondary,
  fontWeight: 500,
  marginTop: theme.spacing(0.5),
  display: "flex",
  alignItems: "center",
}));

const ShiftContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const EmptyShiftIndicator = styled(ErrorOutlineIcon)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: "1rem",
  marginLeft: theme.spacing(0.5),
}));

const ShiftSelect = styled(Select)(({ theme, isAlternate }) => ({
  fontSize: "0.8rem",
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
  marginTop: theme.spacing(0.5),
  minHeight: "1.5rem",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: isAlternate
    ? theme.palette.info.light
    : "rgba(249, 250, 251, 0.8)",
  "&.Mui-focused": {
    backgroundColor: theme.palette.primary.lighter,
  },
  "& .MuiSelect-select": {
    padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
    minHeight: "1.5rem !important",
  },
}));

// Calendar View Component
const CalendarView = ({ scheduleData, availableHosts, onUpdateShift }) => {
  const month = scheduleData.month;
  const year = scheduleData.year;
  // Get day of week for the first day of the month (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfMonth = new Date(year, month - 1, 1).getDay();

  // Get the number of days in the current month
  const daysInMonth = new Date(year, month, 0).getDate();

  // Get the number of days in the previous month
  const daysInPrevMonth = new Date(year, month - 1, 0).getDate();

  // Create calendar array with days from previous month, current month, and next month
  const calendarDays = [];

  // Days from previous month
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push({
      day: daysInPrevMonth - firstDayOfMonth + i + 1,
      isCurrentMonth: false,
      isPrevMonth: true,
    });
  }

  // Days from current month
  for (let i = 1; i <= daysInMonth; i++) {
    // Find schedule data for this day
    const dayData = scheduleData.days.find((d) => d.day === i) || {
      day: i,
      shift1: { name: "" },
      shift2: { name: "" },
    };

    calendarDays.push({
      ...dayData,
      day: i,
      isCurrentMonth: true,
    });
  }

  // Days from next month
  const remainingDays = 42 - calendarDays.length; // 6 rows of 7 days
  for (let i = 1; i <= remainingDays; i++) {
    calendarDays.push({
      day: i,
      isCurrentMonth: false,
      isNextMonth: true,
    });
  }

  // Handle shift change
  const handleShiftChange = (day, shiftKey, event) => {
    const newValue = event.target.value;

    // Find the existing day data
    const dayData = scheduleData.days.find((d) => d.day === day) || {
      day,
      shift1: { name: "" },
      shift2: { name: "" },
    };

    // Determine if this is an alternate shift (preserve the alternate status if it exists)
    const isAlternate = dayData[shiftKey]?.alternate || false;

    // Call the update function
    onUpdateShift(day, shiftKey, {
      name: newValue,
      alternate: isAlternate,
    });
  };

  // Toggle the alternate status
  const toggleAlternateStatus = (day, shiftKey) => {
    // Find the existing day data
    const dayData = scheduleData.days.find((d) => d.day === day);
    if (!dayData) return;

    // Get current data for this shift
    const currentShift = dayData[shiftKey] || { name: "" };

    // Call the update function with toggled alternate status
    onUpdateShift(day, shiftKey, {
      name: currentShift.name,
      alternate: !currentShift.alternate,
    });
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      <CalendarGrid>
        {/* Day headers */}
        <DayHeader>Sun</DayHeader>
        <DayHeader>Mon</DayHeader>
        <DayHeader>Tue</DayHeader>
        <DayHeader>Wed</DayHeader>
        <DayHeader>Thu</DayHeader>
        <DayHeader>Fri</DayHeader>
        <DayHeader>Sat</DayHeader>

        {/* Calendar days */}
        {calendarDays.map((day, index) => (
          <CalendarDay
            key={index}
            isOtherMonth={!day.isCurrentMonth}
            elevation={0}
          >
            <DayNumber>{day.day}</DayNumber>

            {day.isCurrentMonth && (
              <ShiftContainer>
                {/* Shift 1 */}
                <ShiftLabel>
                  S1:
                  {!day.shift1?.name && (
                    <Tooltip title="Empty shift needs to be assigned">
                      <EmptyShiftIndicator />
                    </Tooltip>
                  )}
                </ShiftLabel>
                <FormControl fullWidth size="small">
                  <ShiftSelect
                    value={day.shift1?.name || ""}
                    onChange={(e) => handleShiftChange(day.day, "shift1", e)}
                    displayEmpty
                    isAlternate={day.shift1?.alternate}
                    renderValue={(selected) => {
                      if (!selected) return "Select host";
                      return day.shift1?.alternate ? `${selected}**` : selected;
                    }}
                    onClick={(e) => {
                      // Detect right-click to toggle alternate status
                      if (e.button === 2) {
                        e.preventDefault();
                        toggleAlternateStatus(day.day, "shift1");
                      }
                    }}
                    onContextMenu={(e) => {
                      // Prevent context menu and toggle alternate status on right click
                      e.preventDefault();
                      toggleAlternateStatus(day.day, "shift1");
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {availableHosts.map((host) => (
                      <MenuItem key={host} value={host}>
                        {host}
                      </MenuItem>
                    ))}
                  </ShiftSelect>
                </FormControl>

                {/* Shift 2 */}
                <ShiftLabel>
                  S2:
                  {!day.shift2?.name && (
                    <Tooltip title="Empty shift needs to be assigned">
                      <EmptyShiftIndicator />
                    </Tooltip>
                  )}
                </ShiftLabel>
                <FormControl fullWidth size="small">
                  <ShiftSelect
                    value={day.shift2?.name || ""}
                    onChange={(e) => handleShiftChange(day.day, "shift2", e)}
                    displayEmpty
                    isAlternate={day.shift2?.alternate}
                    renderValue={(selected) => {
                      if (!selected) return "Select host";
                      return day.shift2?.alternate ? `${selected}**` : selected;
                    }}
                    onClick={(e) => {
                      // Detect right-click to toggle alternate status
                      if (e.button === 2) {
                        e.preventDefault();
                        toggleAlternateStatus(day.day, "shift2");
                      }
                    }}
                    onContextMenu={(e) => {
                      // Prevent context menu and toggle alternate status on right click
                      e.preventDefault();
                      toggleAlternateStatus(day.day, "shift2");
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {availableHosts.map((host) => (
                      <MenuItem key={host} value={host}>
                        {host}
                      </MenuItem>
                    ))}
                  </ShiftSelect>
                </FormControl>
              </ShiftContainer>
            )}
          </CalendarDay>
        ))}
      </CalendarGrid>

      <Box sx={{ mt: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Right-click on a shift to toggle temporary coverage (** status).
        </Typography>
      </Box>
    </Box>
  );
};

export default CalendarView;
